import type { QueryClient } from '@tanstack/react-query';
import {
  Box,
  Boxes,
  Camera,
  DollarSign,
  Eye,
  Gift,
  LayoutDashboard,
  Package,
  Receipt,
  Shield,
  Ticket,
  Users,
} from 'lucide-react';
import { Link, redirect, type RouteObject } from 'react-router-dom';

import { getGamesQueryOptions } from '@/api/getGames';
import { getSandboxesQueryOptions } from '@/api/getSandboxes';
import { GameBreadcrumbDropdown } from '@/features/game/components/gameBreadcrumbDropdown';
import { SandboxBreadcrumbDropdown } from '@/features/sandbox/components/sandboxBreadcrumbDropdown';
import {
  invalidateQueriesExceptGamesAndSandboxes,
  updateApiDefaults,
} from '@/hooks/useGameSandboxSwitch';
import { sandboxCode, storeGameSandbox } from '@/lib/utils';
import type { Game } from '@/models/game';
import type { PlayerModel } from '@/models/players';

import { GameLayout } from './GameLayout';
import { transactionsRoute } from './transactions/route';
import Dashboard from './dashboard';
import { action as deleteBanAction } from './player/bans/delete';
import { editBanAction } from './player/bans/edit-action';
import { storeRoute } from './store/route';
import { itemRoute } from './items/route';
import { giftRoute } from './gift/route';

export const gameRoute = (queryClient: QueryClient): RouteObject => ({
  path: 'game/:gameId',
  loader: ({ params }) => {
    // When changing game, invalidate queries using shared utility
    invalidateQueriesExceptGamesAndSandboxes(queryClient);
    return queryClient
      .ensureQueryData(getGamesQueryOptions())
      .then((data) => data.games.find((g) => g.game_code === params.gameId));
  },
  handle: {
    crumb: (game: Game) => (
      <GameBreadcrumbDropdown gameCode={game.game_code} gameName={game.name} />
    ),
  },
  children: [
    {
      // Index route: resolve sandbox from localStorage and redirect
      index: true,
      loader: async ({ params }) => {
        const { gameId } = params;
        if (!gameId) {
          throw new Error('Game ID is required');
        }
        // Check localStorage for saved sandbox
        const savedSandbox = sandboxCode(gameId);

        // Fetch available sandboxes
        const sandboxes = await queryClient.ensureQueryData(
          getSandboxesQueryOptions({ game_code: gameId }),
        );

        // Validate saved sandbox exists, otherwise use first available
        const targetSandbox = sandboxes.sandboxes.find(
          (s) => s.code === savedSandbox,
        )
          ? savedSandbox
          : sandboxes.sandboxes[0]?.code;

        if (!targetSandbox) {
          throw new Error('No sandboxes available');
        }

        return redirect(targetSandbox);
      },
    },
    {
      path: ':sandboxId',
      id: 'sandbox', // Used by SandboxBreadcrumbDropdown to get the sandbox match data
      handle: {
        crumb: ({ sandboxId }: { sandboxId: string }) => (
          <SandboxBreadcrumbDropdown sandboxCode={sandboxId} />
        ),
      },
      loader: ({ params }) => {
        const { gameId, sandboxId } = params;
        if (!gameId || !sandboxId) {
          throw new Error('Game ID and sandbox ID are required');
        }
        // Set Axios defaults using shared utility - overwrites previous values on each navigation
        updateApiDefaults(gameId, sandboxId);

        // Persist to localStorage for next visit
        storeGameSandbox(gameId, sandboxId);

        return { sandboxId };
      },
      element: <GameLayout />,
      children: [
        {
          element: (
            <Dashboard
              menu
              items={[
                {
                  link: 'players',
                  label: 'Players',
                  icon: <Users size={20} />,
                  items: [],
                },
                {
                  link: 'items',
                  label: 'All Items',
                  icon: null,
                  items: [
                    {
                      link: 'item',
                      label: 'Item',
                      icon: <Box size={16} />,
                    },
                    {
                      link: 'bundle',
                      label: 'Bundle',
                      icon: <Boxes size={16} />,
                    },
                    {
                      link: 'currency',
                      label: 'Currency',
                      icon: <DollarSign size={16} />,
                    },
                    {
                      link: 'season_pass',
                      label: 'Season Pass',
                      icon: <Ticket size={16} />,
                    },
                  ],
                },
                {
                  link: 'gifts',
                  label: 'Gifts',
                  icon: <Gift size={20} />,
                  items: [],
                  disabled:
                    import.meta.env.VITE_ADMIN_DISABLED_FEATURE.includes(
                      'gifts',
                    ),
                },
                {
                  link: 'transactions',
                  label: 'Transactions',
                  icon: null,
                  items: [
                    {
                      link: 'store',
                      label: 'Store',
                      icon: <Receipt size={20} />,
                    },
                    {
                      link: 'inventory',
                      label: 'Inventory',
                      icon: <Package size={20} />,
                    },
                  ],
                },
                {
                  link: 'store',
                  label: 'Store',
                  disabled:
                    import.meta.env.VITE_ADMIN_DISABLED_FEATURE.includes(
                      'store',
                    ),
                  icon: null,
                  items: [
                    {
                      link: 'snapshot',
                      label: 'Snapshot',
                      icon: <Camera size={16} />,
                    },
                    {
                      link: 'preview',
                      label: 'Preview',
                      icon: <Eye size={16} />,
                    },
                  ],
                },
              ]}
            />
          ),
          children: [
            {
              path: 'players',
              lazy: () => import('./players'),
            },
            { ...itemRoute },
            { ...giftRoute },
            { ...transactionsRoute },
            { ...storeRoute(queryClient) },
          ],
        },
        {
          path: 'players/:playerId',
          handle: {
            crumb: () => (
              <Link to=".." relative="path">
                Players
              </Link>
            ),
          },
          element: (
            <Dashboard
              menu
              items={[
                {
                  link: '../players',
                  label: 'Back to players',
                  icon: <Users size={20} />,
                },
                {
                  link: 'overview',
                  label: 'Overview',
                  icon: <LayoutDashboard size={20} />,
                },
                {
                  link: 'bans',
                  label: 'Bans',
                  icon: <Shield size={20} />,
                },
                {
                  link: 'inventory',
                  label: 'Inventory',
                  icon: <Package size={20} />,
                },
                {
                  link: 'currencies',
                  label: 'Currencies',
                  icon: <DollarSign size={20} />,
                  items: [],
                },
                {
                  link: 'transaction',
                  label: 'Transaction',
                  icon: null,
                  items: [
                    {
                      link: 'store',
                      label: 'Store',
                      icon: <Receipt size={20} />,
                    },
                    {
                      link: 'inventory',
                      label: 'Inventory',
                      icon: <Package size={20} />,
                    },
                  ],
                },
              ]}
            />
          ),
          children: [
            {
              path: '',
              id: 'player',
              handle: {
                crumb: ({ player }: { player: PlayerModel }) =>
                  player.platform_user_id,
              },
              lazy: async () => {
                const { loader, Player } = await import('./player');
                return { loader: loader(queryClient), Component: Player };
              },
              children: [
                {
                  path: 'overview',
                  lazy: () => import('./player/overview/overview'),
                },
                {
                  path: 'bans',
                  lazy: () => import('./player/bans/bans'),
                },
                {
                  path: 'bans/create',
                  handle: {
                    crumb: () => 'Create Ban',
                  },
                  lazy: () => import('./player/bans/edit'),
                  action: editBanAction(queryClient),
                },
                {
                  path: 'bans/:banId/edit',
                  handle: {
                    crumb: () => 'Edit Ban',
                  },
                  lazy: () => import('./player/bans/edit'),
                  action: editBanAction(queryClient),
                },
                {
                  path: 'bans/:banId/edit/delete',
                  action: deleteBanAction,
                },
                {
                  path: 'currencies',
                  lazy: () => import('./player/currencies/currencies'),
                },
                {
                  path: 'inventory',
                  lazy: () => import('./player/inventory/inventory'),
                },
                {
                  path: 'transaction',
                  children: [
                    {
                      path: '',
                      loader: () => redirect('store'),
                    },
                    {
                      path: 'store',
                      lazy: () => import('./player/transaction/store'),
                    },
                    {
                      path: 'inventory',
                      lazy: () => import('./player/transaction/inventory'),
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});
